import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { PagedResult } from './bank-account.proxy.service';

/** One invoice line (Order.API ItemInInvoiceDto) — references a PO item + carries the invoiced quantity. */
export interface PreInvoiceItemDto {
  itemInPurchaseOrderId?: string | null;
  quantity?: number | null;
  purchaseOrderReference?: string | null;
}

/** Trimmed Order.API InvoiceDto — only what the AP "Pre-Invoice" picker needs (#4306). */
export interface PreInvoiceDto {
  id?: string | null;
  invoiceNumber?: string | null;
  providerInvoiceNumber?: string | null;
  /** Provider's fiscal (CAI) document number. */
  fiscalInvoiceNumber?: string | null;
  totalAmount?: number | null;
  /** Order rate to USD — the AP document's exchange rate is taken from the invoice (#4306). */
  exchangeRateToUSD?: number | null;
  /** When the provider issued the invoice — seeds the AP document date, which IS the issue date (#4783). */
  invoiceIssuedDate?: string | null;
  purchaseOrderReferences?: string[] | null;
  purchaseOrderNumbers?: string[] | null;
  items?: PreInvoiceItemDto[];
}

/**
 * CustomerOrderStatus (Order.Models) — only the value AP needs. Order.API registers no
 * JsonStringEnumConverter, so the status must go over the wire as its NUMBER, not its name.
 */
export const ORDER_DOCUMENT_STATUS_APPROVED = 2;

export interface InvoiceOrderSearchCommand {
  invoiceNumber?: string | null;
  providerInvoiceNumber?: string | null;
  fiscalInvoiceNumber?: string | null;
  purchaseOrderReference?: string | null;
  /** CustomerOrderStatus filter (Order.API InvoiceSearch.Statuses) — null/empty = any status. */
  statuses?: number[] | null;
}

/** Order.API Invoice search for the AP "register from Pre-Invoice" flow (#4306). */
@Injectable({ providedIn: 'root' })
export class InvoiceOrderProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/invoice-order';

  /**
   * Search invoices NOT yet consumed by an AP document (excludeConsumed=true).
   *
   * `matchAny` ORs the populated criteria together, so the picker can match one typed term against several
   * numbers at once. Site scope, the consumed exclusion and the status filter are always AND'd server-side,
   * never folded into that OR — see InvoiceRepository.BuildSearchFilter in Order.API. That distinction is
   * what makes it safe to pass `statuses` alongside matchAny: a Draft invoice cannot slip in by matching
   * one of the number terms.
   */
  public searchUnconsumed(
    command: InvoiceOrderSearchCommand,
    pageNumber = 1,
    pageSize = 10,
    matchAny = false,
  ): Observable<ApiResponse<PagedResult<PreInvoiceDto>>> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('excludeConsumed', true)
      .set('matchAny', matchAny);
    return this.httpClient.post<ApiResponse<PagedResult<PreInvoiceDto>>>(`${this.baseUrl}/search`, command, { params });
  }
}
